import { useState } from 'react';
import { useSubroute } from '../../lib/subroute.js';
import { useSession } from '../../lib/auth/session.js';
import {
  useAllInvoices, useInvoice, useInvoiceByNumber, type InvoiceFilter,
} from '../../lib/api/hooks-invoices.js';
import { downloadFile } from '../../lib/api/download.js';
import { TableSkeleton, Empty, ErrorBanner, Segmented } from '../../components/ui/Primitives.js';
import { toastError } from '../../components/ui/Toast.js';
import { Icon } from '../../components/ui/Icon.js';
import { formatMoney } from '../../lib/format.js';
import { InvoiceBadge, InvoiceDocument, docDate, printDocument } from './InvoiceDocument.js';
import { InvoiceEditor } from './InvoiceEditor.js';
import { CreditNoteForm, RecordPaymentForm } from './InvoiceActions.js';

/**
 * Invoices across every account, for platform staff (docs/28 B2): drafts, and
 * issued invoices by number at /billing/invoices/INV00012, where they are
 * credited, paid and downloaded. From an admin address only (nginx).
 */
export function InvoicesPage() {
  const route = useSubroute();
  const number = route.rest[0] ?? null;

  return (
    <>
      <div className="arc-page-head">
        <div>
          <h1 className="arc-h1">Invoices</h1>
          <p className="arc-muted" style={{ marginTop: 3 }}>Drafts, issued invoices, credit notes and payments</p>
        </div>
      </div>
      {number
        ? <StaffInvoice number={number} onBack={() => route.go([])} />
        : <InvoiceList onOpen={(n) => route.go([n])} />}
    </>
  );
}

const FILTERS: ReadonlyArray<readonly [InvoiceFilter, string]> = [
  ['all', 'All'], ['draft', 'Drafts'], ['unpaid', 'Unpaid'], ['overdue', 'Overdue'], ['paid', 'Paid'],
];

function InvoiceList({ onOpen }: { onOpen: (number: string) => void }) {
  const can = useSession((s) => s.can);
  const [show, setShow] = useState<InvoiceFilter>('all');
  /** A draft's id, 'new', or nothing open. */
  const [editing, setEditing] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const invoices = useAllInvoices(show);
  const draft = useInvoice(editing && editing !== 'new' ? editing : null);
  const busy = !!editing || recording;

  return (
    <>
      {editing && (editing === 'new' || draft.data) && (
        <InvoiceEditor key={editing} draft={editing === 'new' ? null : draft.data ?? null}
          onClose={() => setEditing(null)}
          onIssued={(n) => { setEditing(null); onOpen(n); }} />
      )}
      {draft.error && <ErrorBanner error={draft.error} />}
      {recording && (
        <RecordPaymentForm tenantId={null} onCancel={() => setRecording(false)}
          onDone={(receipt) => { setRecording(false); setNotice(`Payment recorded as receipt ${receipt}.`); }} />
      )}

      <div className="arc-card">
        <div className="arc-toolbar">
          <Segmented label="Show" value={show} onChange={setShow} options={FILTERS} />
          <div className="arc-spacer" />
          {notice && <span className="arc-muted">{notice}</span>}
          {can('payment:record') && !busy && (
            <button type="button" className="arc-btn arc-btn-sm"
              onClick={() => { setNotice(null); setRecording(true); }}>
              Record a payment
            </button>
          )}
          {can('invoice:issue') && !busy && (
            <button type="button" className="arc-btn arc-btn-primary arc-btn-sm"
              onClick={() => { setNotice(null); setEditing('new'); }}>
              <Icon name="plus" size={13} /> New invoice
            </button>
          )}
        </div>
        {invoices.error ? <div className="arc-card-body"><ErrorBanner error={invoices.error} /></div> : (
          <div className="arc-table-wrap">
            <table className="arc-table">
              <thead>
                <tr>
                  <th>Invoice</th><th>Account</th><th>Date</th><th>Due</th><th>Status</th>
                  <th className="arc-td-right">Total</th>
                  <th className="arc-td-right">Due now</th>
                </tr>
              </thead>
              {invoices.data === undefined ? <TableSkeleton query={invoices} cols={7} /> : (
                <tbody>
                  {invoices.data?.data.map((i) => (
                    <tr key={i.id} className="arc-clickable"
                      onClick={() => (i.number ? onOpen(i.number) : setEditing(i.id))}>
                      <td className="arc-mono" style={{ fontWeight: 600 }}>
                        {i.number ?? <span className="arc-subtle">draft</span>}
                      </td>
                      <td>{i.account_name}</td>
                      <td className="arc-num arc-subtle">{docDate(i.issue_date)}</td>
                      <td className="arc-num arc-subtle">{docDate(i.due_date)}</td>
                      <td><InvoiceBadge invoice={i} /></td>
                      <td className="arc-td-right arc-num">{formatMoney(i.total, i.currency)}</td>
                      <td className="arc-td-right arc-num">
                        {i.status === 'draft' ? '—' : formatMoney(i.amount_due, i.currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              )}
            </table>
          </div>
        )}
        {invoices.data !== undefined && invoices.data?.data.length === 0 && (
          <Empty icon="invoice" title="No invoices" hint="Start one with New invoice." />
        )}
      </div>
    </>
  );
}

function StaffInvoice({ number, onBack }: { number: string; onBack: () => void }) {
  const can = useSession((s) => s.can);
  const invoice = useInvoiceByNumber(number);
  const [action, setAction] = useState<'credit' | 'payment' | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const i = invoice.data;
  const payable = !!i && (i.status === 'issued' || i.status === 'part_paid');

  const download = (path: string, filename: string) => {
    void downloadFile(path, filename).catch(toastError);
  };

  return (
    <>
      <div className="arc-row" style={{ gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" onClick={onBack}>
          <Icon name="arrowLeft" size={13} /> Invoices
        </button>
        {notice && <span className="arc-muted">{notice}</span>}
        <div className="arc-spacer" />
        {i && !action && payable && can('payment:record') && (
          <button type="button" className="arc-btn arc-btn-sm" onClick={() => { setNotice(null); setAction('payment'); }}>
            Record a payment
          </button>
        )}
        {i && !action && i.status !== 'credited' && can('invoice:issue') && (
          <button type="button" className="arc-btn arc-btn-sm" onClick={() => { setNotice(null); setAction('credit'); }}>
            Credit note
          </button>
        )}
        {i && (
          <button type="button" className="arc-btn arc-btn-sm"
            onClick={() => download(`/v1/billing/invoices/${i.id}/pdf`, `${number}.pdf`)}>
            Download PDF
          </button>
        )}
        {i && <button type="button" className="arc-btn arc-btn-sm" onClick={printDocument}>Print</button>}
      </div>

      {i && action === 'credit' && (
        <CreditNoteForm invoice={i} onCancel={() => setAction(null)}
          onDone={() => { setAction(null); setNotice('Credit note issued and emailed.'); }} />
      )}
      {i && action === 'payment' && (
        <RecordPaymentForm tenantId={i.tenant_id} focusInvoiceId={i.id} onCancel={() => setAction(null)}
          onDone={(receipt) => { setAction(null); setNotice(`Payment recorded as receipt ${receipt}.`); }} />
      )}

      {invoice.isLoading
        ? <div className="arc-card arc-card-body"><div className="arc-skeleton" style={{ height: 320 }} /></div>
        : invoice.error
          ? <ErrorBanner error={invoice.error} />
          : i ? (
            <InvoiceDocument invoice={i} creditNoteAction={(note) => (
              <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                onClick={() => download(`/v1/billing/credit-notes/${note.id}/pdf`, `${note.number}.pdf`)}>
                PDF
              </button>
            )} />
          ) : null}
    </>
  );
}
